<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { Textarea } from '~/components/ui/textarea'
import { Label } from '~/components/ui/label'
import { useTypoReport } from '~/composables/useTypoReport'
import { useApiPost } from '~/composables/useApiPost'
import { toast } from '~/components/ui/toast/use-toast'

const { t } = useI18n()
const config = useRuntimeConfig()

const {
  isDialogOpen,
  selectedText,
  tryOpenDialog,
  closeDialog,
  isServicePageActive,
  getSelectionText
} = useTypoReport()

const comment = ref('')
const commentError = ref('')
const isSubmitting = ref(false)

// Floating button state (mobile)
const floatingButtonStyle = ref<{ top: string; left: string } | null>(null)
const hasSelection = ref(false)

const updateFloatingButton = () => {
  if (isServicePageActive()) {
    hasSelection.value = false
    return
  }

  const text = getSelectionText()
  if (!text) {
    hasSelection.value = false
    floatingButtonStyle.value = null
    return
  }

  hasSelection.value = true

  const selection = window.getSelection()
  if (selection && selection.rangeCount > 0) {
    const rect = selection.getRangeAt(0).getBoundingClientRect()
    // position: fixed — viewport-relative, clamped to avoid going off-screen
    floatingButtonStyle.value = {
      top: `${Math.max(8, rect.top - 40)}px`,
      left: `${Math.max(8, rect.left)}px`
    }
  }
}

onMounted(() => {
  document.addEventListener('selectionchange', updateFloatingButton)
  document.addEventListener('mouseup', updateFloatingButton)
})

onUnmounted(() => {
  document.removeEventListener('selectionchange', updateFloatingButton)
  document.removeEventListener('mouseup', updateFloatingButton)
})

const validateComment = (): boolean => {
  if (!comment.value.trim()) {
    commentError.value = t('feature.typo_report.comment_required')
    return false
  }
  commentError.value = ''
  return true
}

const onCommentInput = () => {
  if (commentError.value) {
    commentError.value = ''
  }
}

const onSubmit = async () => {
  if (!validateComment()) return

  isSubmitting.value = true

  const fingerprintStore = useFingerprintStore()
  const body: Record<string, unknown> = {
    url: window.location.href,
    selected_text: selectedText.value,
    comment: comment.value.trim(),
    fingerprint: fingerprintStore.visitorId
  }

  const { data, error } = await useApiPost<{ message?: string }>('/typo-reports', body)

  isSubmitting.value = false

  if (error) {
    toast({
      title: error._data?.message ?? t('feature.typo_report.error_message'),
      variant: 'destructive'
    })
  } else {
    toast({ title: data?.message ?? t('feature.typo_report.success_message') })
    closeDialog()
    comment.value = ''
  }
}

const onCancel = () => {
  closeDialog()
  comment.value = ''
  commentError.value = ''
}

const commentLength = computed(() => comment.value.length)
const isSubmitDisabled = computed(
  () => isSubmitting.value || !selectedText.value || !comment.value.trim()
)
</script>

<template>
  <!-- Floating button — appears on mobile when text is selected -->
  <Teleport to="body">
    <button
      v-if="hasSelection && floatingButtonStyle && !isDialogOpen"
      class="fixed z-50 hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg touch:block"
      :style="floatingButtonStyle"
      @mousedown.prevent="tryOpenDialog"
    >
      {{ $t('feature.typo_report.mobile_button') }}
    </button>
  </Teleport>

  <!-- Dialog -->
  <Dialog :open="isDialogOpen" @update:open="(val) => !val && onCancel()">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ $t('feature.typo_report.dialog_title') }}</DialogTitle>
      </DialogHeader>

      <div class="space-y-4">
        <!-- Selected text preview -->
        <blockquote
          v-if="selectedText"
          class="line-clamp-4 border-l-4 border-gray-200 pl-3 text-sm italic text-gray-500"
        >
          {{ selectedText }}
        </blockquote>

        <!-- Comment field (required) -->
        <div class="space-y-1.5">
          <Label>{{ $t('feature.typo_report.comment_label') }}</Label>
          <Textarea
            v-model="comment"
            :placeholder="$t('feature.typo_report.comment_placeholder')"
            :maxlength="1000"
            rows="3"
            :class="commentError ? 'border-red-500' : ''"
            @input="onCommentInput"
          />
          <div class="mt-1 flex justify-between">
            <span v-if="commentError" class="text-sm text-red-500">{{ commentError }}</span>
            <span class="ml-auto text-xs text-gray-400">{{ commentLength }} / 1000</span>
          </div>
        </div>

      </div>

      <DialogFooter class="gap-2">
        <Button variant="outline-gray" :disabled="isSubmitting" @click="onCancel">
          {{ $t('common.cancel') }}
        </Button>
        <Button :disabled="isSubmitDisabled" @click="onSubmit">
          <span
            v-if="isSubmitting"
            class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
          />
          {{ $t('common.submit') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
